import { useCallback, useContext, useMemo, useState } from 'react';
import Select from 'react-select';
import type { ActionMeta, MultiValue } from 'react-select';
import styled from '@emotion/styled';
import {
  ProjectClipsFiltersContext,
  ProjectClipsTranscriptSearchContext,
  SetProjectClipsFiltersContext,
} from '../../contexts/project-clips/Context';
import Button from '../presentation/Button/Button';

type FilterItem = { id: number; name: string };

type Props = {
  onSubmit: () => void;
};

export const SourceFilterMenu = ({ onSubmit }: Props) => {
  const transcripts = useContext(ProjectClipsTranscriptSearchContext);
  const filters = useContext(ProjectClipsFiltersContext);
  const setFilters = useContext(SetProjectClipsFiltersContext);

  const [selectedIds, setSelectedIds] = useState<number[]>(filters.transcriptIds);
  const [inputValue, setInputValue] = useState('');

  const selected = useMemo(
    () => transcripts.filter((t) => selectedIds.includes(t.id)),
    [selectedIds, transcripts]
  );

  const handleChange = useCallback((value: MultiValue<FilterItem>, _meta: ActionMeta<FilterItem>) => {
    setSelectedIds(value.map((v) => v.id));
  }, []);

  const handleInputChange = useCallback((v: string) => setInputValue(v), []);

  const handleSubmit = useCallback(() => {
    setFilters({ transcriptIds: selected.map((s) => s.id) });
    onSubmit();
  }, [onSubmit, selected, setFilters]);

  return (
    <Menu>
      <Select<FilterItem, true>
        components={{ DropdownIndicator: null }}
        value={selected}
        inputValue={inputValue}
        isMulti
        options={transcripts}
        placeholder="Search transcripts..."
        getOptionLabel={(o) => o.name}
        getOptionValue={(o) => `${o.id}`}
        onChange={handleChange}
        onInputChange={handleInputChange}
      />
      <Footer>
        <Button size="small" variant="brick" color="primary" onClick={handleSubmit}>
          Apply
        </Button>
      </Footer>
    </Menu>
  );
};

SourceFilterMenu.displayName = 'ProjectClips.SourceFilterMenu';

const Menu = styled.div`
  background-color: var(--pri-01);
  border: 1px solid var(--gray-l);
  border-radius: 5px;
  box-shadow: 0 2px 6px 2px #3c404326;
  box-sizing: border-box;
  padding: 12px;
  width: 375px;
`;

const Footer = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
`;
